import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export interface ContextItem {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    /** A line above this item. */
    separator?: boolean;
    danger?: boolean;
    /** A hint on the right (a shortcut). */
    hint?: string;
}

/** A right-click menu at a screen position. It closes on a click elsewhere, Escape, scrolling or resizing, and moves to stay on screen. */
export function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: ContextItem[]; onClose: () => void }) {
    const ref = useRef<HTMLDivElement>(null);
    const [at, setAt] = useState({ left: x, top: y });

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        setAt({ left: Math.max(4, Math.min(x, window.innerWidth - el.offsetWidth - 4)), top: Math.max(4, Math.min(y, window.innerHeight - el.offsetHeight - 4)) });
        el.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    }, [x, y, items.length]);

    useEffect(() => {
        const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
        const key = (e: KeyboardEvent) => {
            if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
            e.preventDefault();
            const list = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
            const i = list.indexOf(document.activeElement as HTMLButtonElement);
            list[(i + (e.key === "ArrowDown" ? 1 : -1) + list.length) % list.length]?.focus();
        };
        window.addEventListener("mousedown", down, true);
        window.addEventListener("contextmenu", down, true);
        window.addEventListener("keydown", key);
        window.addEventListener("resize", onClose);
        window.addEventListener("wheel", onClose, { passive: true });
        return () => {
            window.removeEventListener("mousedown", down, true);
            window.removeEventListener("contextmenu", down, true);
            window.removeEventListener("keydown", key);
            window.removeEventListener("resize", onClose);
            window.removeEventListener("wheel", onClose);
        };
    }, [onClose]);

    return createPortal(
        <div ref={ref} role="menu" style={at} className="fixed z-[70] min-w-52 rounded-md border bg-card p-1 text-sm text-card-foreground shadow-lg" onContextMenu={(e) => e.preventDefault()}>
            {items.map((it, i) => (
                <div key={i}>
                    {it.separator ? <div className="my-1 h-px bg-border" role="separator" /> : null}
                    <button
                        type="button"
                        role="menuitem"
                        disabled={it.disabled}
                        onClick={() => { onClose(); it.onClick?.(); }}
                        className={cn("flex w-full items-center justify-between gap-6 rounded px-2 py-1.5 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40", it.danger && "text-red-700 dark:text-red-400")}
                    >
                        <span>{it.label}</span>
                        {it.hint ? <span className="text-xs text-muted-foreground">{it.hint}</span> : null}
                    </button>
                </div>
            ))}
        </div>,
        document.body
    );
}
